import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Dados da guia de exame, editáveis pela operadora (21/10/2026).
// Admissão "aguardando preenchimento" chega à Convenia sem CPF, endereço, filial...
// e a guia saía com dados que não eram da pessoa (unidade padrão). Aqui:
//  - aviso do que a Convenia ainda NÃO tem;
//  - todos os campos da guia editáveis — o que a operadora grava VENCE a Convenia na
//    guia em PDF e no e-mail; apagar o campo editado volta ao valor da Convenia;
//  - os obrigatórios da clínica (CPF, nascimento, cargo, RG quando exigido) travam o envio.
const CAMPOS = [
  ['cpf', 'CPF', 'text', '000.000.000-00', 'col-md-4'],
  ['nascimento', 'Data de nascimento', 'date', '', 'col-md-4'],
  ['rg', 'RG', 'text', 'Número do RG', 'col-md-4'],
  ['cargo', 'Cargo', 'text', '', 'col-md-6'],
  ['setor', 'Setor', 'text', '', 'col-md-6'],
  ['endereco', 'Endereço (rua, nº, bairro, cidade/UF, CEP)', 'text', '', 'col-12'],
  ['cidade', 'Cidade/UF onde mora', 'text', 'Ex.: Lavras/MG', 'col-md-4'],
  ['telefone', 'Telefone', 'text', '(00) 00000-0000', 'col-md-4'],
  ['sexo', 'Sexo', 'text', 'Masculino / Feminino', 'col-md-4'],
  ['filial', 'Unidade/filial', 'text', 'Ex.: Filial Lavras', 'col-md-4'],
  ['filial_cnpj', 'CNPJ da unidade', 'text', '00.000.000/0000-00', 'col-md-4'],
  ['matricula', 'Matrícula', 'text', '', 'col-md-2'],
  ['pis', 'PIS', 'text', '', 'col-md-2'],
];
const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brParaIso = (v) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v || '');
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (v || '');
};

const ComplementoGuiaExame = ({ clienteId, conveniaId, faltando, onSalvo }) => {
  const [col, setCol] = useState(null);
  const [falta, setFalta] = useState(faltando || []);
  const [valores, setValores] = useState({});
  const [iniciais, setIniciais] = useState({});
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');

  const aplicar = useCallback((c, f) => {
    setCol(c);
    if (f) setFalta(f);
    const ini = {};
    CAMPOS.forEach(([k]) => { ini[k] = k === 'nascimento' ? brParaIso(c?.[k]) : (c?.[k] || ''); });
    setIniciais(ini);
    setValores(ini);
    if ((c?.sem_convenia || []).length || (f || []).length) setAberto(true);
  }, []);

  const carregar = useCallback(() => {
    if (!clienteId || !conveniaId) return;
    axios.get(`${API}/api/bporh/exames/solicitar/`, { params: { cliente_id: clienteId, convenia_id: conveniaId }, headers: headers() })
      .then((r) => aplicar(r.data.colaborador, r.data.faltando || []))
      .catch(() => setErro('Não foi possível ler os dados da guia'));
  }, [clienteId, conveniaId, aplicar]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { if (faltando) setFalta(faltando); }, [faltando]);

  const mudados = CAMPOS.map(([k]) => k).filter((k) => (valores[k] || '') !== (iniciais[k] || ''));

  const salvar = () => {
    const corpo = {};
    mudados.forEach((k) => { corpo[k] = valores[k] || ''; });
    setSalvando(true); setErro(''); setOk('');
    axios.post(`${API}/api/bporh/exames/solicitar/`,
      { cliente_id: Number(clienteId), complemento: { convenia_id: conveniaId, ...corpo } }, { headers: headers() })
      .then((res) => {
        aplicar(res.data.colaborador, res.data.faltando || []);
        setOk('Dados da guia salvos — a guia em PDF e o e-mail já usam os novos dados');
        if (onSalvo) onSalvo(res.data);
      })
      .catch((e) => setErro(e?.response?.data?.mensagem || 'Não foi possível salvar'))
      .finally(() => setSalvando(false));
  };

  if (!col) return null;
  const semConvenia = col.sem_convenia || [];
  const editados = new Set(col.editados || []);
  const rotulo = Object.fromEntries(CAMPOS.map(([k, r]) => [k, r]));

  return (
    <div className="rounded p-3 mb-3" style={{
      background: semConvenia.length || falta.length ? '#fff8e6' : '#f8f9fa',
      border: `1px solid ${semConvenia.length || falta.length ? '#f1be48' : '#dee2e6'}` }}>
      {semConvenia.length > 0 && (
        <div className="small mb-2" style={{ color: '#6c5400' }}>
          <i className="fas fa-triangle-exclamation me-2" style={{ color: '#b5560e' }}></i>
          <b>Atenção: a Convenia ainda não tem estes dados do colaborador:</b> {col.sem_convenia_rotulos.join(', ')}.
          Confira e preencha abaixo antes de enviar — sem isso a guia sai com dados que não são dele
          (ex.: a unidade padrão do cliente).
        </div>
      )}
      {falta.length > 0 && (
        <div className="small mb-2 fw-semibold" style={{ color: '#a11d2b' }}>
          <i className="fas fa-ban me-2"></i>A clínica não agenda sem: {falta.map((k) => rotulo[k] || k).join(', ')}.
        </div>
      )}
      <button type="button" className="btn btn-link btn-sm p-0 fw-bold text-decoration-none" style={{ color: '#00205b' }}
        onClick={() => setAberto((a) => !a)}>
        <i className={`fas fa-chevron-${aberto ? 'down' : 'right'} me-1`}></i>
        Dados da guia (editáveis){editados.size > 0 && <span className="badge bg-light text-muted border ms-2">{editados.size} editado(s)</span>}
      </button>
      {aberto && (
        <>
          <div className="row g-2 mt-1">
            {CAMPOS.map(([k, r, tipo, ph, larg]) => {
              const daConvenia = (col.convenia || {})[k];
              return (
                <div className={larg} key={k}>
                  <label className="form-label small fw-bold mb-1">
                    {r}
                    {editados.has(k) && <span className="badge ms-1" style={{ background: '#e3f2fd', color: '#0a6ea1' }}>editado</span>}
                    {!editados.has(k) && semConvenia.includes(k) && (
                      <span className="badge ms-1" style={{ background: '#fdeaec', color: '#a11d2b' }}>não está na Convenia</span>
                    )}
                  </label>
                  <input type={tipo} className="form-control form-control-sm" placeholder={ph}
                    value={valores[k] || ''} onChange={(e) => setValores((v) => ({ ...v, [k]: e.target.value }))} />
                  {editados.has(k) && (
                    <div className="small text-muted" style={{ fontSize: '.72rem' }}>
                      Convenia: {daConvenia || '— vazio —'} · apague para voltar ao da Convenia
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {erro && <div className="small text-danger mt-2">{erro}</div>}
          {ok && <div className="small text-success mt-2">{ok}</div>}
          <button type="button" className="btn btn-sm btn-success mt-2" disabled={salvando || !mudados.length} onClick={salvar}>
            <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-check'} me-1`}></i>
            Salvar dados da guia{mudados.length ? ` (${mudados.length})` : ''}
          </button>
        </>
      )}
    </div>
  );
};

export default ComplementoGuiaExame;
